/* Content for the reference hub: Parks & Playgrounds. */

export interface Tier1Park {
  slug: string
  name: string
  area: string
  image: string
  imageAlt: string
  hook: string
  ages: string
  shade: string
  restrooms: string
  parking: string
}

export const tier1Parks: Tier1Park[] = [
  {
    slug: 'kids-world-alameda-park',
    name: 'Kids’ World, Alameda Park',
    area: 'Downtown',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/a2e92af4-1802-4884-ad23-e4cadc03aa04.jpg',
    imageAlt:
      'A large wooden castle playground under oak trees with a child climbing a turret',
    hook: 'The wooden castle everyone grew up on. Fully fenced, deep oak shade, and the only downtown playground where you can genuinely sit down for ten minutes.',
    ages: '1–9',
    shade: 'Full',
    restrooms: 'Yes',
    parking: 'Free street',
  },
  {
    slug: 'shipwreck-playground-chase-palm-park',
    name: 'Shipwreck Playground',
    area: 'Waterfront',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/122beb38-2d7d-4fd5-8832-a5c92af79e43.jpg',
    imageAlt:
      'A coastal playground with a whale skeleton climbing structure on sand under palm trees',
    hook: 'Whale bones to climb and a 1916 carousel next door. Almost no shade after eleven, so treat this as a morning park and bring the beach as a backup.',
    ages: '2–9',
    shade: 'Little',
    restrooms: 'Yes',
    parking: 'Metered lot',
  },
  {
    slug: 'stow-grove-park',
    name: 'Stow Grove Park',
    area: 'Goleta',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/0510acee-bfdc-4820-9220-624917f095b9.jpg',
    imageAlt: 'A grove of redwood trees with picnic tables and a grassy clearing',
    hook: 'Redwoods in Goleta, which never stops being strange. The coolest park in the county on a hot day and the best place we know for a first two-wheeler.',
    ages: '2–10',
    shade: 'Full',
    restrooms: 'Yes',
    parking: 'Free lot',
  },
  {
    slug: 'elings-park',
    name: 'Elings Park',
    area: 'The Mesa',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/fa81c602-235f-45ef-9219-68a18a14ae8d.jpg',
    imageAlt:
      'A hilltop park overlooking a coastal city and the ocean with dry golden grass',
    hook: 'Up on the hill with a view over the whole city. Windy most afternoons — bring a layer even in August — but unbeatable for kite flying and big-kid energy.',
    ages: '3–10',
    shade: 'Little',
    restrooms: 'Yes',
    parking: 'Free lot',
  },
]

export interface Tier2Park {
  id: string
  name: string
  area: string
  ages: string
  cost: string
  blurb: string
  shade: string
  restrooms: string
  parking: string
  tags: string[]
}

export const tier2Parks: Tier2Park[] = [
  {
    id: 't2-1',
    name: 'Manning Park',
    area: 'Montecito',
    ages: '0–6',
    cost: 'Free',
    blurb:
      'Small, quiet and almost always empty. Big lawn for a picnic and a toddler structure that isn’t overwhelming for new walkers.',
    shade: 'Partial',
    restrooms: 'Yes',
    parking: 'Free lot',
    tags: ['Free', 'Toddler-friendly', 'Quiet'],
  },
  {
    id: 't2-2',
    name: 'Franceschi Park',
    area: 'Riviera',
    ages: '4–12',
    cost: 'Free',
    blurb:
      'More hillside garden than playground, but the best sunset spot in town with kids who can handle a short trail on their own.',
    shade: 'Partial',
    restrooms: 'No',
    parking: 'Free street',
    tags: ['Free', 'View'],
  },
  {
    id: 't2-3',
    name: 'Tucker’s Grove',
    area: 'Goleta',
    ages: '2–10',
    cost: 'Free',
    blurb:
      'The county’s birthday-party park. Reservable group areas, a creek to poke at, and enough oak shade to survive a July afternoon.',
    shade: 'Full',
    restrooms: 'Yes',
    parking: 'Free lot',
    tags: ['Free', 'Shady', 'Birthday parties'],
  },
  {
    id: 't2-4',
    name: 'Orpet Park',
    area: 'Riviera',
    ages: '1–7',
    cost: 'Free',
    blurb:
      'A hidden pocket park most locals have never found. Tiny, fully shaded and a good bet when everywhere else is packed.',
    shade: 'Full',
    restrooms: 'No',
    parking: 'Free street',
    tags: ['Free', 'Shady', 'Quiet'],
  },
  {
    id: 't2-5',
    name: 'Girsh Park',
    area: 'Goleta',
    ages: '2–12',
    cost: 'Free',
    blurb:
      'Big open fields and a large modern structure. No shade to speak of, so this is a before-ten or after-four park in summer.',
    shade: 'None',
    restrooms: 'Yes',
    parking: 'Free lot',
    tags: ['Free', 'Big kids'],
  },
  {
    id: 't2-6',
    name: 'Shoreline Park',
    area: 'The Mesa',
    ages: '2–10',
    cost: 'Free',
    blurb:
      'Bluff-top lawns, whale watching from the bench, and stairs down to a small beach at low tide. Windy, but the view earns it.',
    shade: 'Partial',
    restrooms: 'Yes',
    parking: 'Free street',
    tags: ['Free', 'View', 'Beach access'],
  },
]

export const tier3Parks = [
  {
    area: 'Downtown & Riviera',
    items: ['Alice Keck Park Memorial Garden', 'Ortega Park', 'Bohnett Park', 'Parma Park'],
  },
  {
    area: 'The Mesa & Westside',
    items: ['La Mesa Park', 'Oak Park', 'Plaza del Mar', 'Honda Valley Park'],
  },
  {
    area: 'Goleta & Isla Vista',
    items: ['Lake Los Carneros', 'Stow House lawn', 'Camino Real Park', 'Ellwood Mesa'],
  },
  {
    area: 'Montecito & Carpinteria',
    items: ['Manning Park annex', 'Carpinteria Skate Park', 'Viola Fields', 'El Carro Park'],
  },
]

export const hubFilters = [
  { group: 'Age', options: ['0–2', '3–5', '6–9', '10+'] },
  { group: 'Area', options: ['Downtown', 'Waterfront', 'The Mesa', 'Montecito', 'Goleta'] },
  { group: 'Good for', options: ['Free', 'Shady', 'Fenced', 'Birthday parties', 'View'] },
]

export const hubFaqs = [
  {
    question: 'Which Santa Barbara playground is best for toddlers?',
    answer:
      'Kids’ World at Alameda Park. It is fully fenced, deeply shaded, and has restrooms within sight of the play structure — the three things that matter most before age three.',
  },
  {
    question: 'Are there any fenced playgrounds in Santa Barbara?',
    answer:
      'Kids’ World is the only fully fenced playground downtown. Manning Park in Montecito and Orpet Park on the Riviera are small enough that a runner cannot get far, which is the next best thing.',
  },
  {
    question: 'Which parks have real shade in summer?',
    answer:
      'Stow Grove and Tucker’s Grove in Goleta have genuine tree cover all day. Kids’ World and Orpet Park are shaded by oaks. Everything on the waterfront is exposed after about eleven in the morning.',
  },
]
